import React, { useEffect, useMemo, useState } from "react"
import moment from "moment-timezone"
import { Alert, Box, Button, CircularProgress, Container, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material"
import { ContentCopy, Save } from "@mui/icons-material"
import { DesktopDatePicker, LocalizationProvider } from "@mui/lab"
import AdapterMoment from "@mui/lab/AdapterMoment"

import { ISTableHeaderCell, ISTableRow, ISTableTitle } from "../../../Utils/ISTable"
import { getToken } from "../../../../utils/fetchApi"
import { PersonObjectivePrizePerson, PersonObjectivePrizeResponse } from "../../../../models/PersonObjectivePrize"

const TIMEZONE = "America/Argentina/Buenos_Aires"

const PersonObjectivePrizeAdmin: React.FC = () => {
  const [targetMonth, setTargetMonth] = useState(moment().tz(TIMEZONE).startOf("month"))
  const [people, setPeople] = useState<PersonObjectivePrizePerson[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [copying, setCopying] = useState(false)
  const [changed, setChanged] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const targetMonthKey = targetMonth.format("YYYY-MM")
  const busy = loading || saving || copying

  const request = async (url: string, options?: RequestInit) => {
    const token = getToken()

    if (!token) {
      throw new Error("No se encontró el token de autenticación.")
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
        ...(options?.headers ?? {})
      }
    })

    let data: any = {}

    try {
      data = await response.json()
    } catch {
      data = {}
    }

    if (!response.ok) {
      throw new Error(data?.error ?? "Ocurrió un error procesando la solicitud.")
    }

    return data
  }

  const loadPrizes = async () => {
    setLoading(true)
    setError(null)
    setMessage(null)

    try {
      const params = new URLSearchParams({ month: targetMonthKey })
      const data = await request(`/objectives/person-prizes?${params.toString()}`) as PersonObjectivePrizeResponse

      setPeople((data.people ?? []).map(person => ({
        ...person,
        objective1Prize: Number(person.objective1Prize ?? 0),
        objective2Prize: Number(person.objective2Prize ?? 0)
      })))

      setChanged(false)
    } catch (err) {
      setPeople([])
      setError(err instanceof Error ? err.message : "Error obteniendo los premios.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPrizes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetMonthKey])

  const updatePrize = (personId: number, field: "objective1Prize" | "objective2Prize", value: number) => {
    setPeople(previous =>
      previous.map(person => person.personId === personId ? { ...person, [field]: value } : person)
    )

    setChanged(true)
    setError(null)
    setMessage(null)
  }

  const validationError = useMemo(() => {
    for (const person of people) {
      if (!Number.isFinite(person.objective1Prize) || person.objective1Prize < 0) {
        return `El premio de Objetivo 1 de ${person.personName} es inválido.`
      }

      if (!Number.isFinite(person.objective2Prize) || person.objective2Prize < 0) {
        return `El premio de Objetivo 2 de ${person.personName} es inválido.`
      }
    }

    return null
  }, [people])

  const savePrizes = async () => {
    if (validationError) {
      setError(validationError)
      return
    }

    setSaving(true)
    setError(null)
    setMessage(null)

    try {
      await request(`/objectives/person-prizes?month=${targetMonthKey}`, {
        method: "PUT",
        body: JSON.stringify({
          people: people.map(person => ({
            personId: person.personId,
            objective1Prize: Number(person.objective1Prize || 0),
            objective2Prize: Number(person.objective2Prize || 0)
          }))
        })
      })

      setChanged(false)
      setMessage(`Premios de ${targetMonth.format("MM/YYYY")} guardados correctamente.`)
      await loadPrizes()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error guardando los premios.")
    } finally {
      setSaving(false)
    }
  }

  const copyPrevious = async () => {
    setCopying(true)
    setError(null)
    setMessage(null)

    try {
      const data = await request(`/objectives/person-prizes/copy-previous?month=${targetMonthKey}`, {
        method: "POST"
      }) as PersonObjectivePrizeResponse

      await loadPrizes()

      setMessage(
        data.copiedFrom
          ? `Premios copiados desde ${moment(data.copiedFrom, "YYYY-MM").format("MM/YYYY")}.`
          : "Premios del mes anterior copiados correctamente."
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error copiando los premios del mes anterior.")
    } finally {
      setCopying(false)
    }
  }

  return (
    <Container maxWidth={false} sx={{ height: "100%" }}>
      <Paper sx={{ borderRadius: "10px", margin: "10px" }}>
        <ISTableTitle title="Premios por objetivo" />

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ padding: 2 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
              views={["month", "year"]}
              inputFormat="MM/YYYY"
              label="Mes"
              value={targetMonth}
              disabled={busy}
              onChange={date => {
                if (date) {
                  setTargetMonth(moment(date).tz(TIMEZONE).startOf("month"))
                }
              }}
              renderInput={params => <TextField {...params} required size="small" sx={{ minWidth: 220 }} />}
            />
          </LocalizationProvider>

          <Button
            size="large"
            variant="outlined"
            startIcon={<ContentCopy />}
            disabled={busy || changed}
            onClick={copyPrevious}
            style={{ textTransform: "none" }}
          >
            Copiar mes anterior
          </Button>

          <Button
            size="large"
            startIcon={<Save />}
            disabled={busy || !changed || !!validationError}
            onClick={savePrizes}
            style={{ textTransform: "none" }}
          >
            Guardar
          </Button>
        </Stack>

        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2">
            Configure el premio máximo mensual de cada persona para cada objetivo en el que participa.
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ margin: "10px" }}>
          {error}
        </Alert>
      )}

      {message && (
        <Alert severity="success" sx={{ margin: "10px" }}>
          {message}
        </Alert>
      )}

      {validationError && !error && (
        <Alert severity="warning" sx={{ margin: "10px" }}>
          {validationError}
        </Alert>
      )}

      <Paper sx={{ borderRadius: "10px", margin: "10px", overflow: "hidden" }}>
        <ISTableTitle title={`Premios - ${targetMonth.format("MM/YYYY")}`} />

        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow selected>
                  <ISTableHeaderCell>Persona</ISTableHeaderCell>
                  <ISTableHeaderCell align="right">Objetivo 1</ISTableHeaderCell>
                  <ISTableHeaderCell align="right">Objetivo 2</ISTableHeaderCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {people.map(person => (
                  <ISTableRow key={person.personId} color="default">
                    <TableCell>
                      <Typography fontWeight="bold">
                        {person.personName}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      {person.participatesObjective1 ? (
                        <TextField
                          type="number"
                          size="small"
                          value={person.objective1Prize}
                          disabled={busy}
                          inputProps={{ min: 0, step: 1000 }}
                          onChange={event => updatePrize(person.personId, "objective1Prize", Number(event.target.value))}
                          sx={{ width: 180 }}
                        />
                      ) : (
                        <Typography color="text.secondary">No participa</Typography>
                      )}
                    </TableCell>

                    <TableCell align="right">
                      {person.participatesObjective2 ? (
                        <TextField
                          type="number"
                          size="small"
                          value={person.objective2Prize}
                          disabled={busy}
                          inputProps={{ min: 0, step: 1000 }}
                          onChange={event => updatePrize(person.personId, "objective2Prize", Number(event.target.value))}
                          sx={{ width: 180 }}
                        />
                      ) : (
                        <Typography color="text.secondary">No participa</Typography>
                      )}
                    </TableCell>
                  </ISTableRow>
                ))}

                {people.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No hay personas configuradas para participar de objetivos.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  )
}

export default PersonObjectivePrizeAdmin